import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles, Search } from "lucide-react";
import api from "@/lib/api";
import HeroSignup from "@/components/HeroSignup";
import NewsPulse from "@/components/NewsPulse";
import InternalAd from "@/components/InternalAd";
import { useAuth } from "@/context/AuthContext";
import { useKarmStats } from "@/hooks/useKarmStats";
import { useSEO } from "@/lib/useSEO";

// ─── Logos used in the marquee strip (Clearbit favicons, monochrome via CSS) ───
const MARQUEE_COMPANIES = [
  ["Goldman Sachs", "goldmansachs.com"],
  ["Bank of America", "bankofamerica.com"],
  ["HSBC", "hsbc.com"],
  ["JPMorgan Chase", "jpmorganchase.com"],
  ["Citi", "citi.com"],
  ["Barclays", "barclays.com"],
  ["Deutsche Bank", "db.com"],
  ["Fidelity", "fidelity.com"],
  ["Wells Fargo", "wellsfargo.com"],
  ["Mastercard", "mastercard.com"],
  ["Visa", "visa.com"],
  ["Amazon", "amazon.com"],
  ["NVIDIA", "nvidia.com"],
  ["Palo Alto Networks", "paloaltonetworks.com"],
  ["Accenture", "accenture.com"],
  ["Deloitte", "deloitte.com"],
  ["EY", "ey.com"],
  ["BNY Mellon", "bnymellon.com"],
  ["Mizuho", "mizuho-fg.com"],
  ["NatWest", "natwestgroup.com"],
];

// ─── Editorial section headline (italic Lora, Tangle-style) ───
const SectionTitle = ({ children, kicker, testId }) => (
  <div className="mb-8 flex items-baseline justify-between gap-4 flex-wrap" data-testid={testId}>
    <div>
      {kicker && <p className="small-caps mb-2">{kicker}</p>}
      <h2 className="font-serif text-3xl md:text-4xl italic tracking-tight" style={{ color: "var(--karm-ink)" }}>
        {children}
      </h2>
    </div>
  </div>
);

// ─── Editorial job card — list-style, no shadows, just a bottom divider ───
const EditorialJobCard = ({ job, testId }) => {
  const domain =
    job.company_domain ||
    (job.company || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(0, 24) + ".com";
  return (
    <Link
      to={`/jobs/${job.job_id}`}
      data-testid={testId || `home-job-card-${job.job_id}`}
      className="group block py-6 border-b transition-colors duration-200"
      style={{ borderColor: "var(--karm-paper-edge)" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--karm-ivory-warm)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div className="flex items-start gap-5">
        <img
          src={`https://logo.clearbit.com/${domain}`}
          alt=""
          loading="lazy"
          className="h-10 w-10 rounded-sm object-contain mt-1 grayscale group-hover:grayscale-0 transition"
          onError={(e) => {
            e.currentTarget.style.visibility = "hidden";
          }}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span className="small-caps">{job.company}</span>
            {job.sector && (
              <span
                className="text-[10px] px-2 py-0.5 lowercase"
                style={{ background: "var(--karm-ivory-warm)", color: "var(--karm-ink-muted)" }}
              >
                {job.sector}
              </span>
            )}
          </div>
          <h3
            className="font-serif text-xl md:text-2xl leading-snug mb-1 group-hover:text-[color:var(--karm-vermillion)]"
            style={{ color: "var(--karm-ink)" }}
          >
            {job.title}
          </h3>
          <p className="small-caps">
            {job.location}
            {job.posted_age && <span className="opacity-60"> · {job.posted_age}</span>}
          </p>
        </div>
        <ArrowUpRight
          className="h-4 w-4 opacity-0 group-hover:opacity-100 transition mt-2"
          style={{ color: "var(--karm-vermillion)" }}
        />
      </div>
    </Link>
  );
};

// ─── Fresh-on-desk tier card (vertical, 3-up column).
// Tier badge intentionally suppressed per user request — keeps the homepage
// editorial-clean and avoids occasional mis-tagging of edge-case titles.
const FreshTierCard = ({ job }) => {
  const domain =
    (job.company || "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "")
      .slice(0, 24) + ".com";
  return (
    <Link
      to={`/jobs/${job.job_id}`}
      data-testid={`fresh-tier-card-${job.job_id}`}
      className="group block p-4 md:p-6 border-r border-b last:border-r-0 md:border-b-0 transition-colors duration-200"
      style={{ borderColor: "var(--karm-paper-edge)" }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--karm-ivory-warm)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div className="flex items-center gap-2 md:gap-3 mb-2 md:mb-3">
        <img
          src={`https://logo.clearbit.com/${domain}`}
          alt=""
          loading="lazy"
          className="h-6 w-6 md:h-7 md:w-7 rounded-sm object-contain grayscale group-hover:grayscale-0 transition"
          onError={(e) => (e.currentTarget.style.visibility = "hidden")}
        />
        <span className="small-caps">{job.company}</span>
      </div>
      <h4
        className="font-serif text-base md:text-xl leading-snug mb-1.5 md:mb-2 group-hover:text-[color:var(--karm-vermillion)] min-h-[2.25rem] md:min-h-[3rem]"
        style={{ color: "var(--karm-ink)" }}
      >
        {job.title}
      </h4>
      <p className="small-caps">{job.location}</p>
      <div className="mt-2 md:mt-4 flex items-center gap-1 small-caps opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--karm-vermillion)" }}>
        Read more <ArrowUpRight className="h-3 w-3" />
      </div>
    </Link>
  );
};

export default function HomePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const stats = useKarmStats();
  const [freshSections, setFreshSections] = useState([]);
  const [freshGeneratedAt, setFreshGeneratedAt] = useState(null);
  const [heroQuery, setHeroQuery] = useState("");
  const submitHeroSearch = (q) => {
    const term = (q ?? heroQuery).trim();
    navigate(term ? `/jobs?q=${encodeURIComponent(term)}` : "/jobs");
  };
  useSEO({
    title: "KARM — Global GCC Jobs | Build Your Authority",
    description: "The dedicated job search for roles at Global Capability Centre (GCC) employers worldwide — India, US, UK, Europe, Singapore, Australia and more.",
    path: "/",
  });

  useEffect(() => {
    // 15-pick curated homepage grid (3 jobs × 5 sectors, daily-refreshed batch).
    api.get("/jobs/fresh-curated")
      .then((r) => {
        setFreshSections(r.data?.sections || []);
        setFreshGeneratedAt(r.data?.generated_at || null);
      })
      .catch(() => {});
  }, []);

  const freshLabel = freshGeneratedAt
    ? new Date(freshGeneratedAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
    : null;

  return (
    <div className="karm-editorial min-h-screen" data-testid="karm-home">
      {/* ───────── HERO ───────── */}
      <section
        className="relative border-b"
        style={{ borderColor: "var(--karm-paper-edge)" }}
        data-testid="hero-section"
      >
        <div className="max-w-4xl mx-auto px-6 py-24 md:py-32 lg:py-40 text-center">
          {/* Devanagari shloka — Gita 2.47 */}
          <p
            className="font-devanagari text-3xl md:text-5xl leading-loose mb-3 md:mb-6 opacity-90"
            lang="sa"
            data-testid="hero-shloka"
            style={{ color: "var(--karm-ink)" }}
          >
            कर्मण्येवाधिकारस्ते मा फलेषु कदाचन।
          </p>
          <p
            className="font-serif italic text-base md:text-lg mb-14"
            style={{ color: "var(--karm-ink-muted)" }}
            data-testid="hero-translation"
          >
            You have right to act (not to fruit), thence focus and act.
            <span className="mx-2 opacity-60">·</span>
            <span className="small-caps">Bhagavad Gita 2.47</span>
          </p>

          <h1
            className="font-serif text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight mb-8"
            style={{ color: "var(--karm-ink)" }}
            data-testid="hero-headline"
          >
              Find your <em>karma</em> at the world&apos;s<br />top Global Capability Centers.
          </h1>

          <p
            className="font-serif text-lg md:text-xl max-w-2xl mx-auto mb-12 leading-relaxed"
            style={{ color: "var(--karm-ink-muted)" }}
            data-testid="hero-subhead"
          >
            The dedicated job search for roles at Global Capability Centres — worldwide.
          </p>

          {/* Hero search — universal, above every CTA path.
              Tapping a chip or Enter routes to /jobs with q applied. */}
          <div className="max-w-2xl mx-auto mb-8" data-testid="hero-search-wrap">
            <div
              className="flex items-center gap-3 px-4 md:px-5 py-3 md:py-4 border transition-shadow focus-within:shadow-md"
              style={{ background: "var(--karm-ivory)", borderColor: "var(--karm-paper-edge)" }}
            >
              <Search className="h-4 w-4 md:h-5 md:w-5 shrink-0" style={{ color: "var(--karm-ink-muted)" }} />
              <input
                type="text"
                value={heroQuery}
                onChange={(e) => setHeroQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && submitHeroSearch()}
                placeholder='Try "AI Engineer", "MLOps", "United States"…'
                className="flex-1 min-w-0 bg-transparent border-0 outline-none font-serif text-base md:text-lg placeholder:italic"
                style={{ color: "var(--karm-ink)" }}
                aria-label="Search KARM jobs"
                data-testid="hero-search-input"
              />
              <button
                onClick={() => submitHeroSearch()}
                className="hidden md:block small-caps shrink-0 hover:opacity-80 transition-opacity"
                style={{ color: "var(--karm-vermillion)" }}
                data-testid="hero-search-submit"
              >
                Search →
              </button>
            </div>
            {/* Popular chips — one-tap search shortcuts */}
            <div className="mt-3 flex flex-wrap gap-1.5 justify-center" data-testid="hero-search-chips">
              {["AI Engineer", "Machine Learning", "MLOps", "AIML", "United States", "Singapore"].map((t) => (
                <button
                  key={t}
                  onClick={() => submitHeroSearch(t)}
                  className="px-2.5 py-1 text-[11px] border rounded-full transition-colors hover:opacity-80"
                  style={{ borderColor: "var(--karm-paper-edge)", color: "var(--karm-ink-muted)" }}
                  data-testid={`hero-search-chip-${t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {user ? (
            <div className="flex items-center justify-center gap-6 flex-wrap">
              <button
                onClick={() => navigate("/jobs")}
                data-testid="hero-cta-primary"
                className="px-7 py-3 font-sans text-base font-medium transition-opacity hover:opacity-90"
                style={{ background: "var(--karm-vermillion)", color: "var(--karm-ivory)" }}
              >
                Browse AI &amp; ML jobs →
              </button>
            </div>
          ) : (
            <div className="max-w-xl mx-auto">
              <HeroSignup />
            </div>
          )}
        </div>
      </section>

      {/* ───────── TOP EMPLOYERS MARQUEE ───────── */}
      <section
        className="karm-marquee overflow-hidden py-6 border-b"
        style={{ borderColor: "var(--karm-paper-edge)" }}
        data-testid="market-marquee"
      >
        <p className="small-caps text-center mb-5">⸻ Top global employers hiring right now ⸻</p>
        <div className="karm-marquee-track gap-10 items-center">
          {[...MARQUEE_COMPANIES, ...MARQUEE_COMPANIES].map(([name, domain], i) => (
            <div
              key={`${name}-${i}`}
              className="flex items-center gap-3 shrink-0 px-4 py-2 border rounded-sm"
              style={{
                borderColor: "var(--karm-paper-edge)",
                background: "var(--karm-ivory)",
                minWidth: 220,
              }}
              title={name}
              data-testid={`ticker-card-${name}`}
            >
              <img
                src={`https://logo.clearbit.com/${domain}`}
                alt=""
                loading="lazy"
                className="h-8 w-8 object-contain shrink-0"
                onError={(e) => (e.currentTarget.style.visibility = "hidden")}
              />
              <span className="font-serif text-sm font-medium truncate max-w-[160px]" style={{ color: "var(--karm-ink)" }}>
                {name}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* ───────── TRUST BAND ───────── */}
      <section
        className="py-6 border-b text-center"
        style={{ background: "var(--karm-ivory-warm)", borderColor: "var(--karm-paper-edge)" }}
        data-testid="trust-band"
      >
        <p className="small-caps">
          {stats.companies.toLocaleString()}+ GCCs · roles worldwide
        </p>
      </section>

      {/* ───────── FRESH ON THE DESK — 3 picks × 5 sectors, daily-curated batch ───────── */}
      {freshSections.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 py-16" data-testid="fresh-on-desk">
          <div className="mb-12 text-center">
            <p className="small-caps mb-3">Fresh on the desk</p>
            <h2 className="font-serif text-3xl md:text-5xl italic tracking-tight" style={{ color: "var(--karm-ink)" }}>
              Three roles, five lanes, every morning.
            </h2>
            {freshLabel && (
              <p className="small-caps mt-3 opacity-70">
                Curated {freshLabel} · refreshed daily 08:00 IST
              </p>
            )}
          </div>

          {freshSections.map((s, si) => (
            <section
              key={s.slug}
              className={`py-12 ${si > 0 ? "border-t" : ""}`}
              style={{ borderColor: "var(--karm-paper-edge)" }}
              data-testid={`fresh-section-${s.slug}`}
            >
              <div className="mb-6 flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                  <p className="small-caps mb-2">{s.label}</p>
                  <h3 className="font-serif text-2xl md:text-3xl italic tracking-tight" style={{ color: "var(--karm-ink)" }}>
                    {s.label === "BFSI" && "Where the balance sheets are deepest."}
                    {s.label === "Consulting" && "Where decisions become decks."}
                    {s.label === "Media & Telecom" && "Where the signal meets the story."}
                    {s.label === "Pharma & Health" && "Where the molecule meets the market."}
                    {s.label === "Retail" && "Where the customer is still queen."}
                  </h3>
                </div>
                <Link
                  to={`/jobs?sector=${encodeURIComponent(
                    s.label === "Pharma & Health" ? "Healthcare" : s.label === "Media & Telecom" ? "Media" : s.label
                  )}`}
                  className="font-serif italic text-base hover:underline underline-offset-4 shrink-0"
                  style={{ color: "var(--karm-vermillion)" }}
                  data-testid={`fresh-see-all-${s.slug}`}
                >
                  Browse all {s.label} roles →
                </Link>
              </div>

              <div className="grid gap-0 md:grid-cols-3 border-t" style={{ borderColor: "var(--karm-paper-edge)" }}>
                {s.jobs.map((j) => (
                  <FreshTierCard key={j.job_id} job={j} />
                ))}
              </div>
            </section>
          ))}
        </section>
      )}

      {/* ───────── CONTENT STUDIO TEASER (admin-only for now) ───────── */}
      {user?.role === "admin" && (
        <section
          className="max-w-5xl mx-auto px-6 py-20 border-t"
          style={{ borderColor: "var(--karm-paper-edge)" }}
          data-testid="content-studio-teaser"
        >
          <div className="grid md:grid-cols-[1fr_auto] gap-8 items-end">
            <div>
              <p className="small-caps mb-2 inline-flex items-center gap-2">
                <Sparkles className="h-3 w-3" /> AI Content Studio
              </p>
              <h2 className="font-serif text-3xl md:text-4xl italic mb-3" style={{ color: "var(--karm-ink)" }}>
                Build your professional voice.
              </h2>
              <p className="font-serif text-lg" style={{ color: "var(--karm-ink-muted)" }}>
                Posts, carousels, on-brand images. Publish to LinkedIn, X, Instagram. Generated in your tone, in seconds.
              </p>
            </div>
            <Link
              to="/content-studio"
              data-testid="goto-content-studio"
              className="font-serif italic text-lg inline-flex items-center gap-2 hover:underline underline-offset-4"
              style={{ color: "var(--karm-vermillion)" }}
            >
              Open Studio <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      )}

      {/* ───────── JOBS MARKET PULSE (compact widget) ───────── */}
      <NewsPulse />

      {/* ───────── INTERNAL AD — after hero / market pulse ───────── */}
      <div className="px-6 max-w-6xl mx-auto -mt-2 mb-2">
        <InternalAd slot="home_after_hero" />
      </div>

      {/* ───────── SEO AUTHORITY BLOCK ─────────
          Semantic HTML5 <section> + rich internal links help Google, Bing,
          Perplexity, and ChatGPT understand what KARM is and rank us for
          "GCC jobs". Kept editorial in tone so it never feels spammy. */}
      <section
        className="border-t px-6 py-16"
        style={{ borderColor: "var(--karm-paper-edge)", background: "var(--karm-ivory-warm)" }}
        aria-label="GCC Jobs Worldwide — regions, sectors, and topics"
        data-testid="home-seo-authority"
      >
        <div className="max-w-6xl mx-auto">
          <p className="small-caps mb-4">— GCC Jobs, curated worldwide —</p>
          <h2 className="font-serif italic text-3xl md:text-4xl tracking-tight mb-8" style={{ color: "var(--karm-ink)" }}>
            The most comprehensive search for Global Capability Centre roles.
          </h2>
          <div className="grid md:grid-cols-3 gap-10 text-sm" style={{ color: "var(--karm-ink)" }}>
            <div>
              <p className="small-caps mb-3">Browse GCC jobs by region</p>
              <ul className="space-y-1.5 font-serif">
                <li><Link to="/jobs?country=India" className="hover:underline">GCC jobs in India</Link></li>
                <li><Link to="/jobs?country=United%20States" className="hover:underline">GCC jobs in the United States</Link></li>
                <li><Link to="/jobs?country=United%20Kingdom" className="hover:underline">GCC jobs in the United Kingdom</Link></li>
                <li><Link to="/jobs?country=Europe" className="hover:underline">GCC jobs in Europe</Link></li>
                <li><Link to="/jobs?country=Singapore" className="hover:underline">GCC jobs in Singapore</Link></li>
                <li><Link to="/jobs?country=Australia" className="hover:underline">GCC jobs in Australia</Link></li>
              </ul>
            </div>
            <div>
              <p className="small-caps mb-3">Browse GCC jobs by sector</p>
              <ul className="space-y-1.5 font-serif">
                <li><Link to="/jobs?sector=BFSI" className="hover:underline">BFSI &amp; Investment Banking GCCs</Link></li>
                <li><Link to="/jobs?sector=Semiconductors" className="hover:underline">Semiconductors &amp; Hardware GCCs</Link></li>
                <li><Link to="/jobs?sector=Healthcare" className="hover:underline">Healthcare &amp; Life-Sciences GCCs</Link></li>
                <li><Link to="/jobs?sector=Retail" className="hover:underline">Retail &amp; E-commerce GCCs</Link></li>
                <li><Link to="/jobs?sector=Consulting" className="hover:underline">Consulting &amp; Strategy GCCs</Link></li>
                <li><Link to="/jobs?sector=Media%20%26%20Telecom" className="hover:underline">Media &amp; Telecom GCCs</Link></li>
              </ul>
            </div>
            <div>
              <p className="small-caps mb-3">About KARM</p>
              <p className="font-serif leading-relaxed mb-3" style={{ color: "var(--karm-ink-muted)" }}>
                KARM is the dedicated job search for roles at <strong style={{ color: "var(--karm-ink)" }}>Global
                Capability Centres</strong> worldwide. Rankings are computed from your resume, not paid placement.
              </p>
              <ul className="space-y-1.5 font-serif">
                <li><Link to="/companies" className="hover:underline">All 1,100+ GCC employers →</Link></li>
                <li><Link to="/jobs" className="hover:underline">Search live roles worldwide →</Link></li>
                <li><Link to="/news" className="hover:underline">Jobs Market Pulse — latest hiring news →</Link></li>
                <li><Link to="/karmpatra" className="hover:underline">Karmpatra — editorial letters →</Link></li>
                {user?.role === "admin" && (
                  <li><Link to="/pricing" className="hover:underline">KARM Plus — AI Concierge &amp; Content Studio →</Link></li>
                )}
              </ul>
            </div>
          </div>
          <p className="mt-10 font-serif italic text-xs" style={{ color: "var(--karm-ink-muted)" }}>
            KARM is the dedicated search for careers at Global Capability Centres — worldwide.
          </p>
        </div>
      </section>

      {/* ───────── FOOTER ───────── */}
      <footer
        className="border-t mt-0 py-16 px-6"
        style={{ borderColor: "var(--karm-paper-edge)" }}
        data-testid="home-footer"
      >
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-12">
          <div>
            <p className="font-devanagari text-2xl mb-2" style={{ color: "var(--karm-ink)" }}>कर्म</p>
            <p className="font-serif italic text-sm" style={{ color: "var(--karm-ink-muted)" }}>
              The single curated home for jobs at Global Capability Centres — worldwide.
            </p>
          </div>
          <div>
            <p className="small-caps mb-4">Our coverage</p>
            <p className="font-serif text-sm leading-relaxed" style={{ color: "var(--karm-ink-muted)" }}>
              We index {stats.companies.toLocaleString()}+ GCCs, curating roles worldwide.
            </p>
            <Link
              to="/jobs"
              className="font-serif italic text-sm mt-3 inline-block hover:underline underline-offset-4"
              style={{ color: "var(--karm-vermillion)" }}
              data-testid="footer-browse-jobs"
            >
              Browse all roles →
            </Link>
          </div>
          <div>
            <p className="small-caps mb-4">Stay informed</p>
            <p className="font-serif text-sm mb-3" style={{ color: "var(--karm-ink-muted)" }}>
              A weekly digest of the freshest GCC roles, hand-picked.
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                navigate("/auth");
              }}
              className="flex gap-2 border-b"
              style={{ borderColor: "var(--karm-paper-edge)" }}
              data-testid="footer-newsletter-form"
            >
              <input
                type="email"
                placeholder="you@work.com"
                className="flex-1 bg-transparent border-0 outline-none py-2 font-sans text-sm"
                style={{ color: "var(--karm-ink)" }}
                data-testid="footer-newsletter-input"
              />
              <button
                type="submit"
                className="px-3 py-2 transition-opacity hover:opacity-80"
                data-testid="footer-newsletter-submit"
                aria-label="Subscribe"
              >
                <ArrowRight className="h-4 w-4" style={{ color: "var(--karm-vermillion)" }} />
              </button>
            </form>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-12 pt-6 border-t flex items-center justify-between text-xs" style={{ borderColor: "var(--karm-paper-edge)", color: "var(--karm-ink-muted)" }}>
          <span className="small-caps">© KARM · 2026</span>
          <span className="font-serif italic">Made with care in Bengaluru.</span>
        </div>
      </footer>
    </div>
  );
}
